// ============================================================================
// Componente: AutomacaoFaturaCartao.js
// Objetivo: Interface interna para extração de dados da Fatura do Cartão.
// Padrão visual: Financeiro / Verde-azulado
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import {
    CreditCard,
    Download,
    AlertCircle,
    CheckCircle2,
    Loader2,
    FileText,
    Eraser,
    UploadCloud
} from 'lucide-react';

const AutomacaoFaturaCartao = () => {
    const corModulo = '#16a39c';
    const corModuloEscura = '#0f766e';
    const corFundoClaro = '#f0fdfa';
    const corBorda = '#99f6e4';

    const inputFaturaRef = useRef(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [arquivoFatura, setArquivoFatura] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [mensagem, setMensagem] = useState(null);

    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando arquivo...');
    const [isPolling, setIsPolling] = useState(false);
    const [executionId, setExecutionId] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let interval;

        if (isPolling && executionId) {
            interval = setInterval(async () => {
                try {
                    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                    const res = await fetch(`/api/automacoes-financeiro/logs/${executionId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (!res.ok) return;

                    const data = await res.json();
                    if (data.logs && data.logs.length > 0) {
                        const lastLog = data.logs[data.logs.length - 1];
                        const cleanLog = String(lastLog)
                            .replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s*-\s*INFO\s*-\s*|\[.*?\]\s*)/g, '')
                            .trim();

                        setStatusText(cleanLog || lastLog);

                        if (cleanLog.includes('Iniciando leitura')) setProgress(15);
                        else if (cleanLog.includes('Processando página')) setProgress(45);
                        else if (cleanLog.includes('Extração concluída')) setProgress(80);
                        else if (cleanLog.includes('Sucesso')) setProgress(100);
                    }
                } catch (_) {}
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [isPolling, executionId]);

    const handleLimpar = () => {
        setArquivoFatura(null);
        setMensagem(null);
        setProgress(0);
        setStatusText('Aguardando arquivo...');
        setExecutionId(null);
        setIsPolling(false);

        if (inputFaturaRef.current) inputFaturaRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!arquivoFatura) {
            setMensagem({ type: 'error', text: 'Selecione a fatura em PDF antes de gerar a planilha.' });
            return;
        }

        setCarregando(true);
        setMensagem(null);
        setProgress(5);
        setStatusText('Enviando fatura PDF...');

        const newExecutionId = `fatura_${Date.now()}`;
        setExecutionId(newExecutionId);
        setIsPolling(true);

        const formData = new FormData();
        formData.append('executionId', newExecutionId);
        formData.append('fatura_pdf', arquivoFatura);

        try {
            const token = localStorage.getItem('token') || localStorage.getItem('authToken');
            const response = await fetch('/api/automacoes-financeiro/fatura-cartao', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) {
                let errorData = {};
                try {
                    errorData = await response.json();
                } catch (_) {}
                throw new Error(errorData.error || 'Erro ao processar a fatura.');
            }

            const blob = await response.blob();
            const contentDisposition = response.headers.get('content-disposition');
            let fileName = 'Extrato_Fatura_Cartao.xlsx';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.*?)"?(;|$)/);
                if (match?.[1]) fileName = match[1].replace(/['"]/g, '');
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setProgress(100);
            setStatusText('Excel baixado com sucesso!');
            setMensagem({ type: 'success', text: 'Extração concluída! O Excel foi baixado com sucesso.' });
            setArquivoFatura(null);
            if (inputFaturaRef.current) inputFaturaRef.current.value = '';
        } catch (error) {
            setMensagem({ type: 'error', text: error.message || 'Erro de conexão com o servidor.' });
            setStatusText('Falha no processamento.');
        } finally {
            setCarregando(false);
            setTimeout(() => setIsPolling(false), 3000);
        }
    };

    const getProgressBarColor = () => {
        if (mensagem?.type === 'error') return '#ef4444';
        if (mensagem?.type === 'success') return '#10b981';
        return corModulo;
    };

    const cardStyle = {
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '28px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={cardStyle}>
                <div style={{ backgroundColor: corFundoClaro, borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '10px', marginBottom: '24px' }}>
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: corModuloEscura, fontSize: '15px', fontWeight: '800', margin: 0 }}>
                        <FileText size={18} /> Extração de Fatura PDF
                    </h4>
                    <p style={{ color: '#115e59', fontSize: '14px', lineHeight: '1.6', margin: '6px 0 0 0' }}>
                        Faça o upload da fatura original do cartão de crédito em PDF. O sistema extrai as transações e gera uma planilha organizada para conferência.
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{
                            border: `2px dashed ${corBorda}`,
                            borderRadius: '14px',
                            padding: '40px 20px',
                            textAlign: 'center',
                            backgroundColor: '#f8fafc',
                            cursor: carregando ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            position: 'relative'
                        }}>
                            <input
                                ref={inputFaturaRef}
                                type="file"
                                accept=".pdf"
                                onChange={(e) => {
                                    setArquivoFatura(e.target.files?.[0] || null);
                                    setMensagem(null);
                                    setProgress(0);
                                    setStatusText('Aguardando arquivo...');
                                }}
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: carregando ? 'not-allowed' : 'pointer' }}
                                required
                                disabled={carregando}
                            />

                            {!arquivoFatura ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ backgroundColor: '#f1f5f9', padding: '16px', borderRadius: '50%', color: '#94a3b8' }}>
                                        <UploadCloud size={32} />
                                    </div>
                                    <div>
                                        <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#334155' }}>Arraste o PDF aqui ou clique para selecionar</p>
                                        <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>Formato suportado: PDF</p>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ backgroundColor: corFundoClaro, padding: '16px', borderRadius: '50%', color: corModulo }}>
                                        <CreditCard size={32} />
                                    </div>
                                    <div>
                                        <p style={{ margin: '0 0 4px 0', fontWeight: '800', color: '#0f172a' }}>{arquivoFatura.name}</p>
                                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{(arquivoFatura.size / 1024 / 1024).toFixed(2)} MB</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={handleLimpar}
                            disabled={carregando}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 18px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '800', border: '1px solid #cbd5e1', cursor: carregando ? 'not-allowed' : 'pointer', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
                        >
                            <Eraser size={18} /> Limpar
                        </button>

                        <button
                            type="submit"
                            disabled={carregando || !arquivoFatura}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', backgroundColor: carregando || !arquivoFatura ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '800', border: 'none', cursor: carregando || !arquivoFatura ? 'not-allowed' : 'pointer', boxShadow: '0 4px 8px -3px rgba(22, 163, 156, 0.45)', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
                        >
                            {carregando ? <><Loader2 size={18} className="animate-spin" /> Extraindo Dados...</> : <><Download size={18} /> Gerar Planilha Excel</>}
                        </button>
                    </div>
                </form>

                {mensagem && !carregando && (
                    <div style={{
                        backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2',
                        border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                        color: mensagem.type === 'success' ? '#047857' : '#b91c1c',
                        padding: '16px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginTop: '24px',
                        fontSize: '14px',
                        fontWeight: '700'
                    }}>
                        {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                        {mensagem.text}
                    </div>
                )}
            </div>

            {(carregando || progress > 0) && (
                <div style={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {carregando && <Loader2 size={24} color={corModulo} className="animate-spin" />}
                            {!carregando && mensagem?.type === 'success' && <CheckCircle2 size={24} color="#10b981" />}
                            {!carregando && mensagem?.type === 'error' && <AlertCircle size={24} color="#ef4444" />}
                            <div>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>Status da Execução</h4>
                                <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>{statusText}</p>
                            </div>
                        </div>
                        <span style={{ fontSize: '18px', fontWeight: '900', color: getProgressBarColor() }}>{Math.round(progress)}%</span>
                    </div>

                    <div style={{ width: '100%', height: '10px', backgroundColor: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(progress, 100)}%`, height: '100%', backgroundColor: getProgressBarColor(), transition: 'width 0.4s ease' }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutomacaoFaturaCartao;
